import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { pluck } from 'rxjs/operators';
import { Project } from 'src/app/core/objects/project';
import { ProjectService } from 'src/app/core/services/project.service';
import { UserService } from 'src/app/core/services/user.service';
import { LoadingSpaceType, Trucks, WorkSpaceStop } from 'src/app/core/types';
import {
  validateIsStringInArray,
  validateMaxLoadingSpace,
  validateZero,
} from 'src/app/core/validators/validators';
import { Subscription } from 'rxjs';
import * as _ from 'lodash';
import { areWorkspaceStopsChanged } from 'src/app/shared/helpers/workspace-stops.helper';

@Component({
    selector: 'gl-loading-space-air-dimensions',
    templateUrl: './loading-space-air-dimensions.component.html',
    styleUrls: ['./loading-space-air-dimensions.component.scss'],
    standalone: false
})
export class LoadingSpaceAirDimensionsComponent implements OnInit, OnChanges, OnDestroy {
  @Output() public submitEmitter = new EventEmitter();
  @Output() public formData = new EventEmitter();
  @Output() public deleteLoadingSpace = new EventEmitter();
  @Input() public chosenLoadingSpace: LoadingSpaceType;
  @Input() public loadingSpace: LoadingSpaceType;
  @Input() public typeLoadingSpace: number;
  @Input() public isEdit: boolean;
  @Input() public changeType: boolean;
  @Input() public hideDelete: boolean;
  @Input() public isMyLoadingSpace = false;
  @Input() public savedStops: WorkSpaceStop[];
  @Input() public initialStops: WorkSpaceStop[];
  public saveLoadingForm: SaveLoadingForm;
  public form: DimensionsForm = this.generateLoadingSpaceForm();
  public currentLoadingSpace: LoadingSpaceType;
  public loadingSpaces: LoadingSpaceType[];
  private maxLimit = 99;
  public isFormChanged = false;
  private oldFormValue = '';
  private subscription: Subscription;
  private formSubscription: Subscription;
  private loadingSpaceNames: string[];

  constructor(
    private projectService: ProjectService,
    private translate: TranslateService,
    private userService: UserService,
  ) {}

  public ngOnInit(): void {
    this.userService.user.pipe(pluck('trucks')).subscribe((trucks: Trucks) => {
      this.loadingSpaces = trucks.standard.concat(trucks.user);
      this.loadingSpaceNames = this.loadingSpaces.map(
        (loadingSpace) => this.translate.instant(loadingSpace.name) as string,
      );
    });
    this.subscription = this.form.valueChanges.subscribe((values) => {
      if (this.oldFormValue !== this.form.get('name').value) {
        this.isFormChanged = true;
      } else {
        this.isFormChanged = false;
      }
      this.formData.emit(values);
    });
    this.saveLoadingForm = new UntypedFormGroup({
      remember: new UntypedFormControl(false),
    });
    this.form.disable();

    this.formSubscription = this.form.controls['name'].valueChanges.subscribe((name: string) => {
      this.currentLoadingSpace = this.loadingSpaces.find(
        (loadingSpace) => this.translate.instant(loadingSpace.name) === name,
      );
      const isNameDirty = this.form.controls['name'].dirty;
      const isNameChanged = this.oldFormValue !== this.form.get('name').value;
      const isValidationDisabled = !(isNameChanged && isNameDirty);
      this.form.controls['name'].setValidators([
        Validators.maxLength(255),
        validateIsStringInArray(this.loadingSpaceNames, isValidationDisabled),
      ]);
      this.form.controls['name'].updateValueAndValidity({ onlySelf: false, emitEvent: false });
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (
      changes.chosenLoadingSpace &&
      changes.chosenLoadingSpace.currentValue !== changes.chosenLoadingSpace.previousValue
    ) {
      this.onChosen(changes.chosenLoadingSpace.currentValue as LoadingSpaceType);
    }
    if (changes.isEdit && changes.isEdit.currentValue) {
      setTimeout(() => this.form.enable(), 0);
    } else if (changes.isEdit && !changes.isEdit.currentValue) {
      setTimeout(() => this.form.disable(), 0);
    }

    if (changes.changeType && changes.changeType.currentValue) {
      this.form.controls.airQuantity.setValidators([]);
      this.form.controls.airQuantity.updateValueAndValidity();
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.formSubscription.unsubscribe();
  }

  public isLoadingSpaceEquel(): boolean {
    return _.isEqual(this.loadingSpace, this.chosenLoadingSpace);
  }

  public areStopsChanged(): boolean {
    return areWorkspaceStopsChanged(this.savedStops, this.initialStops);
  }

  public resetForm(): void {
    this.form.reset();
    this.form.enable();
  }

  public reload(): void {
    this.onChosen(this.chosenLoadingSpace);
    this.form.enable();
  }

  public submit(): void {
    if (this.form.valid) {
      const shouldSave = this.saveLoadingForm.value.remember;
      this.submitEmitter.emit(shouldSave);
      this.isFormChanged = false;
    }
  }

  public emitDeleteLoadingSpace(): void {
    this.deleteLoadingSpace.emit();
  }

  public onChosen(loadingSpace: LoadingSpaceType): void {
    if (!loadingSpace) {
      return;
    }
    this.form.reset();
    this.form.updateValueAndValidity();
    const name = loadingSpace.name ? (this.translate.instant(loadingSpace.name) as string) : '';
    this.form.patchValue({
      name: name,
      airType: loadingSpace.airType,
      airLengthA: loadingSpace.airLengthA,
      airWidth: loadingSpace.airWidth,
      airBaseB: loadingSpace.airBaseB,
      airHeightC: loadingSpace.airHeightC,
      airHeightD: loadingSpace.airHeightD,
      airLoadCapacity: loadingSpace.limit,
      airLoadingSide: loadingSpace.trailerLoadingSide,
      airQuantity: loadingSpace.length ? 1 : null,
    });
    this.oldFormValue = this.form.get('name').value as string;
    this.isFormChanged = false;
  }

  private generateLoadingSpaceForm(): UntypedFormGroup {
    this.projectService.project.subscribe((project: Project) => {
      if (project.workSpaces) {
        this.maxLimit = project.workSpaces.length < 99 ? 99 - project.workSpaces.length : 0;
      } else {
        this.maxLimit = 99;
      }
    });
    return new UntypedFormGroup({
      name: new UntypedFormControl(null, [Validators.maxLength(255)]),
      airType: new UntypedFormControl(null, [Validators.required, validateZero]),
      airLengthA: new UntypedFormControl(null, [Validators.required, validateZero]),
      airWidth: new UntypedFormControl(null, [Validators.required, validateZero]),
      airBaseB: new UntypedFormControl(null, [Validators.required, validateZero]),
      airHeightC: new UntypedFormControl(null, [Validators.required, validateZero]),
      airHeightD: new UntypedFormControl(null, [Validators.required, validateZero]),
      airLoadCapacity: new UntypedFormControl(null, []),
      airLoadingSide: new UntypedFormControl(null),
      airQuantity: new UntypedFormControl(null, [
        Validators.required,
        validateZero,
        validateMaxLoadingSpace(this.maxLimit),
      ]),
    });
  }
}

interface DimensionsForm extends UntypedFormGroup {
  value: {
    name: string;
    airType: number;
    airLengthA: number;
    airWidth: number;
    airBaseB: number;
    airHeightC: number;
    airHeightD: number;
    airLoadCapacity: number;
    airLoadingSide: string;
    airQuantity: number;
  };
}

interface SaveLoadingForm extends UntypedFormGroup {
  value: {
    remember: boolean;
  };
}
